import { noop } from '@sgme/fp';
import classNames from 'classnames';
import { FC, useCallback, useMemo } from 'react';

import type { ClientFilter, ProductFilter } from './Filter.helper';
import { GroupMenu } from './GroupMenu';

interface ClientFilterItemProps extends ClientFilter {
  selectedClientId?: string;
  selectedProductId?: string;
  onSelected(clientId: string, product?: string): void;
  onTilesDeleted(clientId: string, product?: string): void;
}

export const ClientFilterItem: FC<ClientFilterItemProps> = ({
  selectedClientId,
  selectedProductId,
  id,
  name,
  count,
  products,
  onSelected,
  onTilesDeleted,
}) => {
  const isClientSelected = useMemo(() => id === selectedClientId, [id, selectedClientId]);
  const onClientClicked = useCallback(
    () => (isClientSelected ? noop : onSelected(id)),
    [id, isClientSelected, onSelected],
  );
  const onDeleteClientTilesClicked = useCallback(() => onTilesDeleted(id), [id, onTilesDeleted]);

  return (
    <li className="nav-item w-100">
      {isClientSelected ? (
        <>
          <span
            className="nav-link active d-flex flex-row align-items-center clickable"
            id={id}
            onClick={onClientClicked}
            data-e2e={`client-${id}`}
          >
            <em className="icon icon-md">folder_shared</em>
            <span className="ms-2 flex-grow-1 text-truncate" title={name}>
              {name}
            </span>
            <span
              className="badge rounded-pill badge-primary float-end monospace-numbers"
              data-e2e={`client-${id}-count`}
            >
              {count}
            </span>
            <GroupMenu groupId={id} onMenuToggled={onClientClicked} onDeleted={onDeleteClientTilesClicked} />
            <em className="icon icon-md">navigate_next</em>
          </span>
          <ul className="nav nav-underline flex-column nav-hover">
            {products.map(productFilter => (
              <ProductFilterItem
                key={productFilter.id}
                id={productFilter.id}
                name={productFilter.name}
                count={productFilter.count}
                selectedClientId={selectedClientId!}
                selectedProductId={selectedProductId}
                onSelected={onSelected}
                onTilesDeleted={onTilesDeleted}
              />
            ))}
          </ul>
        </>
      ) : (
        <span
          className="nav-link d-flex align-items-center clickable"
          id={id}
          onClick={onClientClicked}
          data-e2e={`client-${id}`}
        >
          <em className="icon icon-md">folder_shared</em>
          <span className="ms-2 flex-grow-1 text-truncate" title={name}>
            {name}
          </span>
          <span
            className="badge rounded-pill badge-primary float-end monospace-numbers"
            data-e2e={`client-${id}-count`}
          >
            {count}
          </span>
          <GroupMenu
            groupId={id}
            onMenuToggled={onClientClicked}
            className="float-end"
            onDeleted={onDeleteClientTilesClicked}
          />
          <em className="icon icon-md">navigate_next</em>
        </span>
      )}
    </li>
  );
};
ClientFilterItem.displayName = 'ClientFilterItem';

interface ProductFilterItemProps extends ProductFilter {
  selectedClientId: string;
  selectedProductId?: string;
  onSelected(clientId: string, product: string): void;
  onTilesDeleted(clientId: string, product: string): void;
}

export const ProductFilterItem: FC<ProductFilterItemProps> = ({
  selectedClientId,
  selectedProductId,
  id,
  name,
  count,
  onSelected,
  onTilesDeleted,
}) => {
  const isProductSelected = useMemo(() => id === selectedProductId, [id, selectedProductId]);

  const onProductClicked = useCallback(
    () => (isProductSelected ? noop : onSelected(selectedClientId, id)),
    [id, isProductSelected, onSelected, selectedClientId],
  );
  const onDeleteProductTilesClicked = useCallback(
    () => onTilesDeleted(selectedClientId, id),
    [id, onTilesDeleted, selectedClientId],
  );

  return (
    <li className="nav-item w-100">
      <span
        className={classNames('nav-link d-flex align-items-center clickable', {
          active: isProductSelected,
          clickable: !isProductSelected,
        })}
        id={id}
        onClick={onProductClicked}
        data-e2e={`client-${selectedClientId}-product-${id}`}
      >
        <em className="icon icon-md">category</em>
        <span className="ms-2 flex-grow-1 text-truncate" title={name}>
          {name}
        </span>
        <span
          className="badge rounded-pill badge-primary float-end monospace-numbers"
          data-e2e={`client-${selectedClientId}-product-${id}-count`}
        >
          {count}
        </span>
        <GroupMenu
          groupId={id}
          onMenuToggled={onProductClicked}
          className="float-end"
          onDeleted={onDeleteProductTilesClicked}
        />
      </span>
    </li>
  );
};
ProductFilterItem.displayName = 'ProductFilterItem';
